import React, { ReactElement, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import LoadingComponent from '../components/Common/LoadingComponent';
import { useAuthStore } from '../stores/authStore';
import { CURRENT_TERMS_VERSION } from '../constants/terms';

const Dashboard = lazy(() => import('../pages/App/Dashboard/Dashboard'));
const Login = lazy(() => import('../pages/Main/Login/Login'));
const ForgotPassword = lazy(() => import('../pages/Main/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/Main/ResetPassword/ResetPassword'));
const Home = lazy(() => import('../pages/Main/Home/Home'));
const About = lazy(() => import('../pages/Main/About/About'));
const Features = lazy(() => import('../pages/Main/Features/Features'));
const Demo = lazy(() => import('../pages/Main/Demo/Demo'));
const TemplatesPage = lazy(() => import('../pages/Main/Templates/Templates'));
const Contact = lazy(() => import('../pages/Main/Contact/Contact'));
const FAQ = lazy(() => import('../pages/Main/FAQ/FAQ'));
const Privacy = lazy(() => import('../pages/Main/Privacy/Privacy'));
const Terms = lazy(() => import('../pages/Main/Terms/Terms'));
const MenuPreview = lazy(() => import('../pages/App/MenuPreview/MenuPreview'));
const MenuView = lazy(() => import('../pages/Main/MenuView/MenuView'));
const MenuRedirect = lazy(() => import('../pages/Main/MenuRedirect/MenuRedirect'));
const NotFound = lazy(() => import('../pages/Main/NotFound/NotFound'));
const CampaignDetail = lazy(() => import('../pages/Main/CampaignDetail/CampaignDetail'));
const ProductDetail = lazy(() => import('../pages/Main/ProductDetail/ProductDetail'));
const AcceptTerms = lazy(() => import('../pages/App/AcceptTerms/AcceptTerms'));

const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#1A1A2E'
  }}>
    <LoadingComponent
      style="spinner" // eslint-disable-line react/style-prop-object
      color="#C5A06A"
      size="large"
      text="Yükleniyor..."
      showLogo={false}
    />
  </div>
);

const RequireAuth = ({ children }: { children: ReactElement }) => {
  const { isAuthenticated, isInitialized, isLoading, user } = useAuthStore();
  const location = useLocation();

  // Auth henüz initialize edilmediyse bekle
  if (!isInitialized || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Yükleniyor...
      </div>
    );
  }

  // Authenticated değilse login'e yönlendir
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Terms gate kontrolü: !termsAccepted || termsVersion != CURRENT_TERMS_VERSION
  const requiresTermsGate = !user?.termsAccepted || user?.termsVersion !== CURRENT_TERMS_VERSION;
  if (requiresTermsGate) {
    // Eğer o an login'deysek (transition tam bitmediyse), returnUrl dashboard kalsın
    const currentPath = location.pathname;
    let returnUrl = '/dashboard';
    if (!currentPath.includes('/login') && !currentPath.includes('/accept-terms')) {
      returnUrl = currentPath + location.search;
    }
    return <Navigate to={`/accept-terms?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return children;
};

export const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Auth */}
    <Route path="/login" element={<Login />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/register" element={<Navigate to="/iletisim" replace />} />
    <Route path="/accept-terms" element={<AcceptTerms />} />
    <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />

    {/* Legacy redirects */}
    <Route path="/giris" element={<Navigate to="/login" replace />} />
    <Route path="/kayit" element={<Navigate to="/iletisim" replace />} />
    <Route path="/panel" element={<Navigate to="/dashboard" replace />} />

    {/* Others */}
    <Route path="/menu-preview" element={<MenuPreview />} />
    <Route path="/r/:menuId" element={<MenuRedirect />} />
    <Route path="/menu-view/:businessId" element={<MenuView mode="business" />} />
    <Route path="/:selectedName/menu" element={<MenuView mode="slug" />} />
    <Route path="/:selectedName/kampanyalar/:campaignSlug" element={<CampaignDetail />} />
    <Route path="/:selectedName/menu/:productSlug" element={<ProductDetail />} />
    {/* Eski route fallback olarak kalsın veya redirect edilebilir, şimdilik kaldırıyorum çünkü çakışabilir */}
    {/* <Route path="/:selectedName/product/:productId" element={<ProductDetail />} /> */}
    <Route path="/:selectedName" element={<MenuView mode="slug" />} />

    {/* Eski ödeme sayfaları kaldırıldı — iletişime yönlendir */}
    <Route path="/payment/success" element={<Navigate to="/iletisim" replace />} />
    <Route path="/payment/failure" element={<Navigate to="/iletisim" replace />} />

    <Route path="/hakkimizda" element={<Layout><About /></Layout>} />
    <Route path="/ozellikler" element={<Layout><Features /></Layout>} />
    <Route path="/fiyatlandirma" element={<Navigate to="/iletisim" replace />} />
    <Route path="/demo" element={<Layout><Demo /></Layout>} />
    <Route path="/sablonlar" element={<Layout><TemplatesPage /></Layout>} />
    <Route path="/iletisim" element={<Layout><Contact /></Layout>} />
    <Route path="/sss" element={<Layout><FAQ /></Layout>} />
    <Route path="/gizlilik" element={<Layout><Privacy /></Layout>} />
    <Route path="/kullanim-sartlari" element={<Layout><Terms /></Layout>} />
    <Route path="/" element={<Layout><Home /></Layout>} />

    {/* 404 - Catch all route - en sonda olmalı */}
    <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
  </Suspense>
);

export default AppRoutes;